import { h, type FunctionalComponent, type SVGAttributes } from 'vue'

type IconProps = SVGAttributes & { size?: number; class?: string }

function base(size: number, children: ReturnType<typeof h>[], props: IconProps) {
  const { size: _s, ...rest } = props
  return h(
    'svg',
    {
      width: size,
      height: size,
      viewBox: '0 0 24 24',
      fill: 'none',
      stroke: 'currentColor',
      'stroke-width': 1.75,
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      'aria-hidden': 'true',
      focusable: 'false',
      ...rest,
    },
    children,
  )
}

function icon(children: (p: IconProps) => ReturnType<typeof h>[]): FunctionalComponent<IconProps> {
  return (props) => base(props.size ?? 16, children(props), props)
}

export const IconGrid = icon(() => [
  h('rect', { x: '3', y: '3', width: '7', height: '7', rx: '1' }),
  h('rect', { x: '14', y: '3', width: '7', height: '7', rx: '1' }),
  h('rect', { x: '3', y: '14', width: '7', height: '7', rx: '1' }),
  h('rect', { x: '14', y: '14', width: '7', height: '7', rx: '1' }),
])

export const IconUsers = icon(() => [
  h('path', { d: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2' }),
  h('circle', { cx: '9', cy: '7', r: '4' }),
  h('path', { d: 'M22 21v-2a4 4 0 0 0-3-3.87' }),
])

export const IconScroll = icon(() => [
  h('path', { d: 'M8 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2h-3' }),
  h('path', { d: 'M8 3a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v1a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1z' }),
  h('path', { d: 'M7 12h10M7 16h6' }),
])

export const IconLogout = icon(() => [
  h('path', { d: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4' }),
  h('path', { d: 'm16 17 5-5-5-5M21 12H9' }),
])

export const IconSearch = icon(() => [
  h('circle', { cx: '11', cy: '11', r: '7' }),
  h('path', { d: 'm21 21-4.3-4.3' }),
])

export const IconPlus = icon(() => [h('path', { d: 'M12 5v14M5 12h14' })])

export const IconTrash = icon(() => [
  h('path', { d: 'M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2' }),
  h('path', { d: 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6' }),
  h('path', { d: 'M10 11v6M14 11v6' }),
])

export const IconCopy = icon(() => [
  h('rect', { x: '9', y: '9', width: '12', height: '12', rx: '2' }),
  h('path', { d: 'M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1' }),
])

export const IconCheck = icon(() => [h('path', { d: 'M20 6 9 17l-5-5' })])

export const IconChevronRight = icon(() => [h('path', { d: 'm9 18 6-6-6-6' })])

export const IconChevronLeft = icon(() => [h('path', { d: 'm15 18-6-6 6-6' })])

export const IconPlay = icon(() => [h('path', { d: 'm6 4 14 8-14 8z' })])

export const IconUpload = icon(() => [
  h('path', { d: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' }),
  h('path', { d: 'm7 9 5-5 5 5M12 4v12' }),
])

export const IconX = icon(() => [h('path', { d: 'M18 6 6 18M6 6l12 12' })])

export const IconAlert = icon(() => [
  h('path', { d: 'M12 3 2 20h20z' }),
  h('path', { d: 'M12 9v5M12 17.5v.01' }),
])

export const IconLock = icon(() => [
  h('rect', { x: '4', y: '10', width: '16', height: '11', rx: '2' }),
  h('path', { d: 'M8 10V7a4 4 0 0 1 8 0v3' }),
])

export const IconRefresh = icon(() => [h('path', { d: 'M21 12a9 9 0 1 1-2.6-6.4M21 4v5h-5' })])

export const IconKey = icon(() => [
  h('circle', { cx: '8', cy: '15', r: '4' }),
  h('path', { d: 'm10.8 12.2 8.2-8.2M17 4l3 3M14 7l3 3' }),
])

export const IconLayers = icon(() => [
  h('path', { d: 'm12 3 9 5-9 5-9-5z' }),
  h('path', { d: 'm3 13 9 5 9-5' }),
])

export const IconFile = icon(() => [
  h('path', { d: 'M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z' }),
  h('path', { d: 'M14 3v5h5' }),
])

export const IconCalendar = icon(() => [
  h('rect', { x: '3', y: '5', width: '18', height: '16', rx: '2' }),
  h('path', { d: 'M3 10h18M8 3v4M16 3v4' }),
])
